import { Fragment } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useAuth } from '../lib/auth'
import { hasSender, senderOf, to } from '../lib/nav'

// The legacy page header, transcribed.
//
// CardioReportManager has no application menu. Every page draws its own
// navigation as the first `<tr>` of its `<form>`, then a centred
// "Cardiac Report Manager" and an orange rule:
//
//     <td><asp:linkbutton id="lnkHome">Home</asp:linkbutton>&nbsp;|
//         <asp:linkbutton id="lnkStudy">Study</asp:linkbutton>&nbsp;|
//         <asp:linkbutton id="lnkLogOff">Log Off</asp:linkbutton></td>
//     ...
//     <td><FONT size="4">Cardiac Report Manager</FONT>
//         <hr color="#ff9966"></td>
//
// — `StudyReport.aspx:16-27`, and the same block copied into fifty-nine other
// pages. The whole vocabulary is six words:
//
//   Home     -> MainStudyList.aspx
//   Study    -> StudyInformation.aspx?STUDYID=<id>
//   Back     -> ViewState("SENDER")
//   Reports  -> the other activity report
//   Scan     -> TestScan.aspx                      (not reproduced, DEC-023)
//   Log Off  -> SignOut + Session.Abandon -> Login.aspx
//
// Two properties of that markup matter more than the labels, and both are
// reproduced here:
//
//  1. **The strip never links to the page you are on.** MainStudyList has no
//     Home; StudyInformation has no Study. It is a list of ways *out*, not a
//     menu, which is why it is short enough to read without looking.
//  2. **It is per page, not per application.** A page with nowhere to go up to
//     shows `Log Off` alone, and the interpretation forms show nothing at all.
//
// `STRIPS` below is that markup read back out of the `.aspx` files, one entry
// per screen. Adding a screen means adding its strip here, not inventing one at
// the call site.

export type StripName =
  | 'worklist'
  | 'studyInformation'
  | 'studyReport'
  | 'worksheet'
  | 'reportView'
  | 'images'
  | 'activity'
  | 'activityTranscription'
  | 'reference'

type ItemName = 'home' | 'study' | 'back' | 'reports' | 'adminPage' | 'logOff'

interface Strip {
  /** `align="right"` on the legacy `<tr>`, or absent (left). */
  align: 'left' | 'right'
  /** The legacy separator: `&nbsp;|` on the study pages, `&nbsp;&nbsp;` elsewhere. */
  separator: 'pipe' | 'space'
  items: ItemName[]
  /** The `.aspx` this strip was read from. */
  source: string
}

const STRIPS: Record<StripName, Strip> = {
  // `MainStudyList.aspx:18` — "Scan  Reports  Log Off". Scan is dropped
  // (DEC-023): it goes to `TestScan.aspx`, which is not reproduced.
  worklist: {
    align: 'right',
    separator: 'space',
    items: ['reports', 'logOff'],
    source: 'MainStudyList.aspx:18',
  },
  // `StudyInformation.aspx:17` — Log Off alone. There is no Home: the legacy's
  // way back to the list from here is the Cancel button (`:794`).
  studyInformation: {
    align: 'right',
    separator: 'space',
    items: ['logOff'],
    source: 'StudyInformation.aspx:17',
  },
  // `StudyReport.aspx:17-21`, left-aligned and pipe-separated, unlike the rest.
  studyReport: {
    align: 'left',
    separator: 'pipe',
    items: ['home', 'study', 'logOff'],
    source: 'StudyReport.aspx:17-21',
  },
  // The same block in all thirty modality worksheets — `ABIReport.aspx:19-23`,
  // `LEAUltrasound.aspx`, `VenousUltrasoundFull.aspx`, `XRay.aspx`, and the
  // `*Update` twin of each. There is no link to the report hub: the legacy
  // leaves it by Generate Report (`ABIReport.aspx.vb:779`) or Cancel.
  worksheet: {
    align: 'left',
    separator: 'pipe',
    items: ['home', 'study', 'logOff'],
    source: 'ABIReport.aspx:19-23',
  },
  // `EchoReportViewer.aspx:17` — "Home  Back", the legacy's only viewer strip.
  // `ReportViewer.aspx` has no form at all.
  reportView: {
    align: 'left',
    separator: 'space',
    items: ['home', 'back'],
    source: 'EchoReportViewer.aspx:17',
  },
  // `VerifyLocation.aspx` is a bare Update Location button with no header of any
  // kind, so there is nothing to transcribe. The worksheet strip is used
  // instead — this page hangs off a study exactly as a worksheet does, and a
  // page with no way out is a defect, not a faithful reproduction. DEC-023.
  images: {
    align: 'left',
    separator: 'pipe',
    items: ['home', 'study', 'logOff'],
    source: 'ABIReport.aspx:19-23 (VerifyLocation.aspx has none)',
  },
  // `ActivityReportViewer.aspx:17` — "Home  AdminPage  Log Off", including the
  // caption `AdminPage`, which is what the link says.
  activity: {
    align: 'right',
    separator: 'space',
    items: ['home', 'adminPage', 'logOff'],
    source: 'ActivityReportViewer.aspx:17',
  },
  // A sibling's strip, and a deliberate one (DEC-023).
  //
  // `ActivityReportTranscriptionAudit.aspx:17` is "Home  Log Off". Reproducing
  // it literally would orphan the other activity report, because the legacy's
  // own route to it is commented out (`MainStudyList.aspx.vb:721-723`) and its
  // only remaining inbound link is from `ActivityReportPDF1.aspx.vb:48`. The
  // strip taken here is `ActivityReportPDFTranscription.aspx:17` — the page
  // this one hands off to — whose Reports link is that same edge.
  activityTranscription: {
    align: 'right',
    separator: 'space',
    items: ['home', 'reports', 'logOff'],
    source: 'ActivityReportPDFTranscription.aspx:17',
  },
  // `AddPhysician.aspx:17-20` — "Back  Home  Log Off". `AdminPage.aspx:17` is
  // "Home  Log Off"; this one page stands for both, because it merges AdminPage
  // with the four child screens that carry the Back. Back draws only when a
  // `?sender=` says where from, which is the legacy's own condition.
  reference: {
    align: 'right',
    separator: 'space',
    items: ['back', 'home', 'logOff'],
    source: 'AddPhysician.aspx:17-20',
  },
}

/**
 * The strip, the banner and the rule, exactly where the legacy puts them.
 *
 * `strip` names the legacy page. `studyId` is required by any strip carrying
 * `Study`. `backTo` is where `Back` falls through to when no `?sender=` was
 * passed.
 *
 * The interpretation forms deliberately have no entry: `CarotidInterpretationForm.aspx`,
 * `EchocardiographyInterpretationForm.aspx` and `TeeInterpretationForm.aspx`
 * draw the banner and the rule with no navigation above them, and leave only
 * through Save and Cancel. Pass `strip={null}` for that.
 */
export default function PageNav({
  strip,
  studyId,
  backTo,
}: {
  strip: StripName | null
  studyId?: number | string
  backTo?: string
}) {
  const [params] = useSearchParams()
  const { signOut, user, may } = useAuth()
  const spec = strip ? STRIPS[strip] : null

  const href = (path: string) => to(path, params)

  function item(name: ItemName) {
    switch (name) {
      case 'home':
        // `lnkHome_Click` -> `Response.Redirect("MainStudyList.aspx")`.
        return <Link key={name} to={href('/')}>Home</Link>
      case 'study':
        // `lnkStudy_Click` -> `StudyInformation.aspx?STUDYID=<id>`.
        return <Link key={name} to={href(`/studies/${studyId}`)}>Study</Link>
      case 'back':
        // `Response.Redirect(ViewState("SENDER"))`.
        return (
          <Link key={name} to={senderOf(params, backTo ?? '/')}>Back</Link>
        )
      case 'reports':
        // `MainStudyList.aspx.vb:720` -> ActivityReportTranscriptionAudit;
        // `ActivityReportPDF1.aspx.vb:48` -> ActivityReportViewer.
        return (
          <Link key={name} to={href(strip === 'worklist' ? '/activity/transcription' : '/activity')}>
            Reports
          </Link>
        )
      case 'adminPage':
        // `ActivityReportViewer.aspx.vb:194` -> `AdminPage.aspx`.
        return <Link key={name} to={href('/reference')}>AdminPage</Link>
      case 'logOff':
        // `lnkLogOff_Click` -> SignOut, Session.Abandon, `Login.aspx`.
        //
        // The signed-in identity is a tooltip rather than a chip on the bar:
        // the legacy never showed who you were, and a permanent user panel is
        // exactly the application-level chrome this component exists to remove.
        // Losing the information altogether would be a regression, so it hangs
        // off the control that acts on it.
        return (
          <button
            key={name}
            type="button"
            className="navlink"
            title={
              user
                ? `${user.username}${
                    user.permission_names.length
                      ? ` · ${user.permission_names.join(', ')}`
                      : ' · no permissions'
                  }`
                : undefined
            }
            onClick={signOut}
          >
            Log Off
          </button>
        )
    }
  }

  // What each destination needs, from the server's own capability map
  // (`domain/auth/policy.py`). Drawn from that list, never decided by it: every
  // destination refuses the request independently (DEC-022). A link hidden here
  // that should not be is a usability bug; a link shown that should not be leads
  // to a 403, not to data.
  //
  // `Log Off` and `Back` need nothing: signing out is not privileged, and Back
  // returns to a page the user has already been on.
  const NEEDS: Partial<Record<ItemName, string>> = {
    home: 'worklist.view',
    study: 'worklist.view',
    reports: 'activity.view',
    adminPage: 'reference.edit',
  }

  const names = (spec?.items ?? []).filter((n) => {
    // `Back` draws only when there is somewhere to go back to. `AdminPage.aspx:17`
    // is "Home  Log Off" and `AddPhysician.aspx:17` is "Back  Home  Log Off" —
    // the same screen family, and the difference is whether a `SENDER` was passed.
    if (n === 'back') return backTo !== undefined || hasSender(params)
    const need = NEEDS[n]
    return !need || may(need)
  })

  const items = names.map(item)

  return (
    <div className="pagehead no-print">
      {spec && (
        <div
          className={`pagenav pagenav-${spec.align} pagenav-${spec.separator}`}
          data-legacy={spec.source}
        >
          {items.map((el, i) => (
            <Fragment key={i}>
              {/* `&nbsp;|` between the study pages' links, `&nbsp;&nbsp;`
                  everywhere else. The gap is the separator on a `space` strip. */}
              {i > 0 && spec.separator === 'pipe' && (
                <span className="pagenav-sep" aria-hidden="true">|</span>
              )}
              {el}
            </Fragment>
          ))}
        </div>
      )}
      <div className="pagebanner">Cardiac Report Manager</div>
      <hr className="pagerule" />
    </div>
  )
}
